import { http } from "./http";

// Phase 7.2 — typed wrapper for the 3 customer-side health-report
// endpoints introduced in Phase 7.1. Shapes mirror the BE DTO so the
// FE composable can pass them through unchanged.

export type LabResultStatus = "ok" | "warn" | "bad";
export type OverallStatus = "ok" | "watch" | "abnormal";
export type HealthCategory =
  | "heart"
  | "sugar"
  | "lung"
  | "lipid"
  | "liver"
  | "kidney"
  | "general";

export interface HealthReportSummary {
  id: string;
  /** Hospital (mhosp.id) that issued the report. */
  hospId: number | null;
  /** Display name of the issuing hospital. */
  hospName: string | null;
  /** True when issued by a hospital other than the login hospital. */
  isOtherHospital: boolean;
  reportDate: string;
  reportType: string;
  overallScore: number | null;
  overallStatus: OverallStatus | null;
  isPublished: boolean;
  /** "IN" (ตรวจใน รพ.) | "OUT" (ตรวจนอก รพ. / ออกหน่วย) */
  serviceSetting?: "IN" | "OUT";
  serviceSettingLabel?: string;
  locationName?: string | null;
  companyCode?: string | null;
  examinerUnit?: string | null;
  /** True when the report contains sensitive labs (HIV, Hepatitis B, etc.) */
  hasSensitiveLab?: boolean;
  requiresInPersonPickup?: boolean;
  pickupInstruction?: string | null;
}

export interface LabResultView {
  testCode: string;
  name: string;
  /** Patient-friendly short name. Falls back to `name` when null. */
  plainName: string | null;
  category: HealthCategory | string;
  description: string | null;
  unit: string | null;
  valueNumeric: number | null;
  valueText: string | null;
  status: LabResultStatus | null;
  note: string | null;
  refMin: number | null;
  refMax: number | null;
  warnMin: number | null;
  warnMax: number | null;
  explainerTitle: string | null;
  explainerText: string | null;
  /** Plain-language meaning per status, shown on the customer card. */
  meaningOk: string | null;
  meaningWarn: string | null;
  meaningBad: string | null;
  recommendations: any | null;
  labDetails: any | null;
  isSensitive?: boolean;
  requiresInPersonPickup?: boolean;
}

export interface CategoryRecord {
  category: HealthCategory | string;
  status: LabResultStatus | null;
  tests: LabResultView[];
}

export interface HealthReportDetail extends HealthReportSummary {
  doctorAdvice: string | null;
  certifiedBy: { hospId: string | null; usrid: string | null };
  source: string;
  vitalsMeasuredAt: string | null;
  vitals: any | null;
  labName: string | null;
  referenceStandard: string | null;
  categories: CategoryRecord[];
  physicalExam?: any;
  chestXray?: string | null;
  summaryFindings?: any;
  recommendations?: any;
}

export interface TestHistoryPoint {
  reportId: string;
  reportDate: string;
  valueNumeric: number | null;
  valueText: string | null;
  status: LabResultStatus | null;
  hospId: number | null;
  hospName: string | null;
  isOtherHospital: boolean;
}

/** Per-hospital breakdown inside one year (viewer's hospital first). */
export interface YearHospitalOption {
  hospId: number;
  hospName: string | null;
  isOtherHospital: boolean;
  /** ISO dates, newest first. */
  reportDates: string[];
  /** Report ids aligned with `reportDates`. */
  reportIds: string[];
}

// Phase 7.5 — multi-year comparison types
export interface YearlyReportSummary {
  year: number;
  reportCount: number;
  firstReportDate: string;
  latestReportDate: string;
  latestOverallStatus: OverallStatus | null;
  latestOverallScore: number | null;
  hospitals: YearHospitalOption[];
}

export interface CompareYearCell {
  value: number | null;
  status: LabResultStatus | null;
  reportId: string | null;
  reportDate: string | null;
  hospId: number | null;
  hospName: string | null;
  isOtherHospital: boolean;
}

export type CompareTrend =
  | "up-bad"
  | "up-warn"
  | "up-ok"
  | "down-bad"
  | "down-warn"
  | "down-ok"
  | "flat"
  | "first-time";

export interface CompareRow {
  testCode: string;
  name: string;
  category: HealthCategory | string;
  unit: string | null;
  /** Default cell per year (login hospital first). */
  byYear: Record<number, CompareYearCell>;
  /** Every hospital's cell per year: byYearHosp[year][hospId]. */
  byYearHosp: Record<number, Record<number, CompareYearCell>>;
  trendArrow: CompareTrend;
  firstTimeTested: boolean;
}

export interface CompareView {
  years: number[];
  rows: CompareRow[];
  yearHospitals: Record<number, YearHospitalOption[]>;
  defaultHospByYear: Record<number, number | null>;
  viewerHospId: number | null;
}

export const healthReportApi = {
  async list(params?: { forPatient?: string; setting?: "IN" | "OUT" | "ALL" }): Promise<HealthReportSummary[]> {
    const { data } = await http.get<HealthReportSummary[]>(
      "/public/health-report",
      { params },
    );
    return data;
  },

  async get(id: string): Promise<HealthReportDetail> {
    const { data } = await http.get<HealthReportDetail>(
      `/public/health-report/${id}`,
    );
    return data;
  },

  async history(
    testCode: string,
    months?: number,
  ): Promise<TestHistoryPoint[]> {
    const params: Record<string, string> = {};
    if (months) params.months = String(months);
    const { data } = await http.get<TestHistoryPoint[]>(
      `/public/health-report/test/${testCode}/history`,
      { params },
    );
    return data;
  },

  // Phase 7.5 — multi-year comparison endpoints.
  async yearly(): Promise<YearlyReportSummary[]> {
    const { data } = await http.get<YearlyReportSummary[]>(
      "/public/health-report/yearly",
    );
    return data;
  },

  async compare(
    opts: {
      testCodes?: string[];
      years?: number;
      aggregation?: "latest" | "average";
    } = {},
  ): Promise<CompareView> {
    const params: Record<string, string> = {};
    if (opts.testCodes?.length) params.testCodes = opts.testCodes.join(",");
    if (opts.years) params.years = String(opts.years);
    if (opts.aggregation) params.aggregation = opts.aggregation;
    const { data } = await http.get<CompareView>(
      "/public/health-report/compare",
      {
        params,
      },
    );
    return data;
  },
};
